import React from 'react';

/**
 * Generic localStorage-backed state. JSON-serialized and resilient to
 * parse/access failures (private mode, quota, malformed value). Keeps in sync
 * across tabs via the `storage` event.
 */
const useLocalStorage = <T>(
    key: string,
    initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] => {
    // Hold the initial value in a ref so callers can pass an inline literal
    // (e.g. `{}`) without re-running the read/subscription on every render.
    const initialValueRef = React.useRef(initialValue);

    const readValue = React.useCallback((): T => {
        if (typeof window === 'undefined') return initialValueRef.current;
        try {
            const raw = window.localStorage.getItem(key);
            return raw === null ? initialValueRef.current : (JSON.parse(raw) as T);
        } catch {
            return initialValueRef.current;
        }
    }, [key]);

    const [storedValue, setStoredValue] = React.useState<T>(readValue);

    const setValue = React.useCallback(
        (value: T | ((prev: T) => T)) => {
            setStoredValue((prev) => {
                const next = value instanceof Function ? value(prev) : value;
                try {
                    window.localStorage.setItem(key, JSON.stringify(next));
                } catch {
                    // ignore write failures (private mode / quota)
                }
                return next;
            });
        },
        [key]
    );

    // Re-read when the key itself changes (e.g. the active org switches and the
    // key is org-scoped) so the value follows the new key without a remount. The
    // ref guard skips the initial mount, which the useState initializer already read.
    const keyRef = React.useRef(key);
    React.useEffect(() => {
        if (keyRef.current !== key) {
            keyRef.current = key;
            setStoredValue(readValue());
        }
    }, [key, readValue]);

    React.useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key === key) {
                setStoredValue(readValue());
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [key, readValue]);

    return [storedValue, setValue];
};

export default useLocalStorage;
